import React, { useEffect , useState } from 'react';
import {
    Button,
    Input,
    Select,
    SelectOption,
    Slider,
    Alerts,
    DateRangePicker,
    Spinner,
    Toast
} from '@kaleyra/emmy';
import Styles from '../../Reports/reports.module.scss';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import ErrorIcon from '../../../../assets/error.svg';
import { isWhatsappSubaccountEnabled, isVoiceSubaccountEnabled, checkResellerAccount, checkEnterpriseAccount, checkResellerSubaccount } from '../../../../utils';
import { useLocation } from 'react-router-dom';

export type MapStatePropsType = {
    sidebarVisibility: any;
};

export type DispatchPropsType = {
    onClose: () => void;
    generateReport: (data: any, callBack: any) => void;
    onLoad: (
        filters?: Record<string, any>,
        searchTerm?: string,
        property?: string
    ) => void;
};

const GenerateReports: React.FC<MapStatePropsType & DispatchPropsType> = ({
    onClose,
    sidebarVisibility,
    generateReport,
    onLoad
}) => {

    const { t } = useTranslation();
    const [filterDText, setFilterDText] = useState<any>();
    const [filterDTextErr, setFilterDTextErr] = useState<string>('');
    const [reportTitle, setReportTitle] = useState<string>('');
    const [reportTitleErr, setReportTitleErr] = useState<string>('');
    const [generate, setGenerate] = useState(false);
    const [channel, setChannel] = useState<any>([]);
    const [currentTime, setCurrentTime] = useState(moment());
    const channelList = [
        'SMS',
        (isVoiceSubaccountEnabled() && checkEnterpriseAccount()) && 'Voice',
        'Credit Usage'
    ];

    const { search } = useLocation();
    const query = new URLSearchParams(search);

    useEffect(() => {
        const reportType = query.get('report-type');
        const dates = query.get('dates');

        if (reportType && reportType === 'credit-usage') {
            setChannel('Credit Usage');
        }

        if (dates) {
            const datesAsArray = dates.split(',');

            const startDate = moment(datesAsArray[0]).startOf('day').format('X');
            const endDate = moment(datesAsArray[1]).startOf('day').format('X');

            setFilterDText(`${startDate},${endDate}`);
        }
    }, []);

    let updatedCreatedDate = filterDText;
    if (filterDText) {
        let createdArr = filterDText.split(",");
        let startDate = moment(createdArr[0], 'X');
        let endDate = moment(createdArr[1], 'X');
        updatedCreatedDate = [startDate, endDate];
    }

    const handleOnSave = () => {
        let from = filterDText.split(',')[0]
        let to = filterDText.split(',')[1]
        setGenerate(true)
        generateReport(({
            filter_range: {
                dateFrom: from,
                dateTo: to
            },
            channel: channel.toLocaleLowerCase(),
            title: reportTitle
        }), (resp: any, success: boolean) => {
            if (success) {
                setGenerate(false)
                Toast(t("KLR02578"), "success");
                onClose();
                onLoad({})

            } else {
                Toast(resp?.data?.message, "error");
                setGenerate(false)
            }
        })
    }

    const checkSaveButtonEnabled = () => {
        if (validateFields('reportTitle') || validateFields('dateRange') || validateFields('channel') || generate) {
            return true;
        }
        return false;
    }

    const getFooter = (): React.ReactNode => {
        return (
            <div className={Styles.footer} >
                <Button type="tertiary" onClick={onClose}>
                    {t("mainCancel")}
                </Button>
                <Button
                    className={Styles.generate}
                    onClick={handleOnSave}
                    disabled={checkSaveButtonEnabled()}
                > {generate ? <Spinner margin={"1rem"} /> : t("KLR0040")}
                </Button>
            </div>
        );
    };

    const validateCheck = (field: string) => {
        if (field === 'reportTitle') {
            if (reportTitle.trim() === '') {
                return t('titleReq');
            }
            if (reportTitle.length < 2) {
                return t("KLR02562");
            }
            if (reportTitle.length > 40) {
                return t("KLR02563");
            }
            return;
        }
        if (field === 'dateRange') {
            if (!updatedCreatedDate) {
                return t('titleReq');
            }
            return;
        }

        if (field === 'channel') {
            if (channel === '') {
                return t('titleReq');
            }
            return;
        }
    }

    const validateFields = (field: string): 'error' | undefined => {
        if (validateCheck(field)) {
            return "error";
        }
        return undefined;
    };

    const setErrors = (field: string) => {
        let errMsg = validateCheck(field);
        if (errMsg) {
            return (
                <div>
                    <img
                        src={ErrorIcon}
                        alt=""
                        className={Styles.errorIcon}
                    />
                    {errMsg}
                </div>
            );
        }
    };

    const handleReportTitleOnBlur = () => {
        let errMsg = validateCheck('reportTitle')
        if (errMsg) {
            setReportTitleErr(errMsg);
            return;
        } else {
            setReportTitleErr('');
            return;
        }
    }

    const handleDateRangeOnBlur = () => {
        let errMsg = validateCheck('dateRange')
        if (errMsg) {
            setFilterDTextErr(errMsg);
            return;
        } else {
            setFilterDTextErr('');
            return;
        }
    }


    const disabledDate = (current: any) => {
        const comparisonTime = moment(currentTime);
        const minusDays = channel === 'Voice' ? -2 : -1;
        return (
            current &&
            (current > comparisonTime.endOf('day') || comparisonTime.add(minusDays, 'days') <= current ||
                current <
                comparisonTime
                    .subtract(180, 'days')
                    .add(1, 'days')
                    .startOf('day'))
        );
    }

    const getContent = () => {

        return (
            <>
                <div className={Styles.reports}></div>
                <Alerts
                    content={t("KLR02552")}
                    type="draft"
                />
                <div style={{ marginTop: 16 }}>
                    <Input
                        placeholder='Report Title'
                        label='Report Title'
                        type='text'
                        maxLength={40}
                        value={reportTitle}
                        className={filterDTextErr ? Styles.errorPadding : undefined}
                        bottomLabelType={reportTitleErr ? validateFields('reportTitle') : undefined}
                        bottomLabel={reportTitleErr ? setErrors('reportTitle') : null}
                        onBlur={() => {
                            handleReportTitleOnBlur();
                        }}
                        onChange={(e: any) => {
                            setReportTitleErr('');
                            setReportTitle(e.target.value);
                        }}
                    />
                </div>

                <Select
                    label={t('reportContent')}
                    placeholder={t('reportContent')}
                    value={channel}
                    onChange={(channel: any) => {
                        setChannel(channel);
                        setFilterDText(undefined);
                        setCurrentTime(moment())
                    }}
                    showBottomLabelSpace={false}
                    style={{ marginBottom: 16 }}
                >
                    {
                        channelList.filter((item) => item).map((channel: any) => {
                            return (
                                <SelectOption
                                    key={channel}
                                    value={channel}
                                >
                                    {channel}
                                </SelectOption>
                            )
                        })
                    }
                </Select>
                {
                    channel.length > 0 &&
                    <>
                        <div className={Styles.daterange}>{t("KLR0035")}</div>
                        <DateRangePicker
                            placeholder={[t('startDate'), t('endDate')]}
                            style={{ width: 368 }}
                            disabledDate={disabledDate}
                            bottomLabelType={filterDTextErr ? validateFields('dateRange') : undefined}
                            bottomLabel={filterDTextErr ? setErrors('dateRange') : null}
                            onBlur={() => {
                                handleDateRangeOnBlur();
                            }}
                            onOk={(e: any) => {
                                let startDate = '';
                                let endDate = '';
                                if (e[0] !== null) {
                                    startDate = e[0].format(
                                        'X'
                                    );
                                }
                                if (e[1] !== null) {
                                    endDate = e[1].endOf('day').format(
                                        'X'
                                    );
                                }
                                if (startDate && endDate) {
                                    setFilterDText(
                                        startDate + ',' + endDate
                                    );
                                }
                            }}
                            value={updatedCreatedDate}
                            formatForDatePicker={'DD MMM YYYY'}
                            hideClearButton
                        />
                    </>
                }
                <Input
                    placeholder={t("KLR02553")}
                    label={t("KLR02553")}
                    disabled={true}
                    value='All Subaccounts'
                />
            </>
        )
    }

    return (
        <Slider
            containerId="root-modal"
            onClose={onClose}
            visibility={sidebarVisibility}
            footer={getFooter()}
            title={t("KLR02554")}
        >
            {getContent()}

        </Slider>
    );
};

export default GenerateReports;
